import { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useHeroParallax } from '@/hooks/useScrollAnimation';

const LOGO_CAPTION_URL = 'https://customer-assets.emergentagent.com/job_espresso-site-9/artifacts/53ds9223_Logo_Petit%20Hem%20%284%29.png';

const PLATFORMS = [
  { key: 'uber_eats_url', name: 'Uber Eats', short: 'UE', color: '#06C167' },
  { key: 'doordash_url', name: 'DoorDash', short: 'DD', color: '#FF3008' },
  { key: 'skip_the_dishes_url', name: 'SkipTheDishes', short: 'SK', color: '#FF6B00' },
];

export default function HeroSection() {
  const { lang, t } = useLanguage();
  const { bgRef, contentRef } = useHeroParallax();
  const [showOrder, setShowOrder] = useState(false);
  const [settings, setSettings] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
  publicApi.getSettings()
    .then((res) => setSettings(res.data))
    .catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowOrder(false);
      }
    };
    if (showOrder) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOrder]);

  return (
    <section id="top" data-testid="hero-section" className="hero-wrapper min-h-[100vh] flex flex-col items-center justify-center relative">
      <div ref={bgRef} className="hero-bg-layer" />

      <div ref={contentRef} className="relative z-10 max-w-lg w-full flex flex-col items-center text-center px-6 will-change-transform">
        <img
          src={LOGO_CAPTION_URL}
          alt="Petit Hem - Cafe Comptoir Viet"
          data-testid="hero-logo"
          className="w-64 md:w-80 mb-10 drop-shadow-2xl animate-fade-in-up opacity-0"
        />
        <p
          data-testid="hero-subtext"
          className="text-base md:text-lg text-[#B0B0B0] leading-relaxed mb-12 max-w-md font-light animate-fade-in-up opacity-0 animation-delay-200"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {t('hero.subtitle')}
        </p>

        {/* Buttons row - side by side */}
        <div className="flex flex-row items-center gap-4 animate-fade-in-up opacity-0 animation-delay-300">
          <a
            href="#menu"
            data-testid="hero-cta"
            className="bg-[#FFD700] text-black hover:bg-[#FFEA00] px-8 py-4 font-bold transition-colors duration-300 text-sm tracking-widest uppercase"
          >
            {t('hero.cta')}
          </a>

          {/* Order Now with dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              data-testid="hero-order-btn"
              onClick={() => setShowOrder(!showOrder)}
              className="border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black px-8 py-4 font-bold transition-colors duration-300 text-sm tracking-widest uppercase flex items-center gap-2"
            >
              {lang === 'fr' ? 'Commander' : 'Order Now'}
              <ChevronDown size={14} className={`transition-transform duration-300 ${showOrder ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown - horizontal icons */}
            {showOrder && (
              <div
                data-testid="order-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 animate-fade-in z-50"
              >
                <div className="flex items-center gap-3 bg-black/90 backdrop-blur-2xl border border-[#FFD700]/15 rounded-full px-5 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
                  {PLATFORMS.map((p) => {
                    const url = settings?.[p.key];
                    const active = !!url;
                    return (
                      <a
                        key={p.key}
                        href={active ? url : undefined}
                        target={active ? '_blank' : undefined}
                        rel={active ? 'noopener noreferrer' : undefined}
                        data-testid={`order-${p.key}`}
                        onClick={(e) => { if (!active) e.preventDefault(); }}
                        title={p.name}
                        className={`group relative flex items-center justify-center w-11 h-11 rounded-full border transition-all duration-300 ${
                          active
                            ? 'border-[#333] hover:border-[#FFD700] hover:scale-110 hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] cursor-pointer'
                            : 'border-[#222] opacity-30 cursor-default'
                        }`}
                      >
                        <span className="text-[10px] font-black tracking-tight" style={{fontFamily:'Bebas Neue', color: active ? p.color : '#444', fontSize:'11px'}}>
                          {p.short}
                        </span>
                        {/* Tooltip */}
                        <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[9px] text-[#B0B0B0] tracking-wider uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          style={{fontFamily:'Manrope'}}>
                          {p.name}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
        <ChevronDown className="text-[#FFD700]/40" size={24} />
      </div>
    </section>
  );
}
